import { useState } from "react";

interface CloneJobTypeDialogProps {
  /** Name of the source job type — pre-populates the input as a hint */
  sourceName: string;
  isSubmitting: boolean;
  onSubmit: (name: string) => Promise<void>;
  onClose: () => void;
}

/**
 * R-98 — Name-only dialog for cloning a Job Type.
 * Mirrors the CreateJobTypeDialog visual pattern (dialog-overlay /
 * dialog-panel / dialog-title / dialog-field / dialog-label / rb-input /
 * rb-footer / rb-btn hierarchy) but collects only the new name.
 */
export function CloneJobTypeDialog({
  sourceName,
  isSubmitting,
  onSubmit,
  onClose,
}: CloneJobTypeDialogProps) {
  const [name, setName] = useState(`${sourceName} Copy`);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    await onSubmit(trimmed);
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <form
        className="dialog-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="clone-jt-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <h2 id="clone-jt-title" className="dialog-title">Clone Job Type</h2>

        <div className="dialog-field">
          <label className="dialog-label" htmlFor="clone-jt-name">
            New Name *
          </label>
          <input
            id="clone-jt-name"
            className="rb-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Invoice Processing Copy"
            maxLength={100}
            autoFocus
          />
        </div>

        <div className="rb-footer">
          <button
            type="button"
            className="rb-btn rb-btn-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rb-btn rb-btn-save"
            disabled={!name.trim() || isSubmitting}
          >
            {isSubmitting ? "Cloning..." : "Clone"}
          </button>
        </div>
      </form>
    </div>
  );
}
